// Asset Import Type Definitions

export type ImportFileStatus = 'pending' | 'processing' | 'complete' | 'error' | 'skipped';

export type DetectedAssetType =
  | 'image'
  | 'audio'
  | 'script'
  | 'font'
  | 'json'
  | 'spine'
  | 'spritesheet'
  | 'unknown';

export interface ImportFileInfo {
  id: string;
  file: File;
  name: string;
  extension: string;
  size: number;
  type: DetectedAssetType;
  mimeType: string;

  // Image specific
  dimensions?: { width: number; height: number };

  // Preview
  previewUrl?: string;
  thumbnail?: string;

  // Import configuration
  targetFolder: string;
  customName?: string;
  assetType: DetectedAssetType;

  // Status
  status: ImportFileStatus;
  progress: number;
  error?: string;

  // Flags
  isSelected: boolean;
  isSpriteSheet: boolean;
  shouldCompress: boolean;
  shouldGenerateThumbnail: boolean;
}

export interface ImportSettings {
  compressImages: boolean;
  compressionQuality: number; // 0-100
  generateThumbnails: boolean;
  thumbnailSize: number;
  keepOriginalNames: boolean;
  autoDetectSpriteSheets: boolean;
  spriteSheetThreshold: { width: number; height: number };
  createSubfolders: boolean;
  overwriteExisting: boolean;
  preserveMetadata: boolean;
}

export interface ImportResult {
  fileId: string;
  success: boolean;
  assetId?: string;
  assetPath?: string;
  error?: string;
  thumbnailPath?: string;
}

export interface ImportProgress {
  total: number;
  completed: number;
  failed: number;
  skipped: number;
  currentFile?: string;
  currentProgress: number;
}

export interface ImportState {
  // Files
  files: ImportFileInfo[];

  // Settings
  settings: ImportSettings;

  // Progress
  isImporting: boolean;
  progress: ImportProgress;

  // Results
  results: ImportResult[];

  // UI State
  isOpen: boolean;
  selectedFileId: string | null;
  targetFolderId: string;
  sortBy: 'name' | 'type' | 'size';
  sortOrder: 'asc' | 'desc';
}

export interface ImportActions {
  // Dialog control
  openDialog: (targetFolderId?: string) => void;
  closeDialog: () => void;

  // File management
  addFiles: (files: FileList | File[]) => void;
  removeFile: (fileId: string) => void;
  clearFiles: () => void;
  selectFile: (fileId: string | null) => void;
  toggleFileSelected: (fileId: string) => void;
  selectAll: () => void;
  deselectAll: () => void;

  // File configuration
  updateFile: (fileId: string, updates: Partial<ImportFileInfo>) => void;
  setFileTargetFolder: (fileId: string, folderId: string) => void;
  setFileAssetType: (fileId: string, type: DetectedAssetType) => void;
  setFileName: (fileId: string, name: string) => void;

  // Settings
  updateSettings: (settings: Partial<ImportSettings>) => void;
  resetSettings: () => void;

  // Import operations
  startImport: () => Promise<void>;
  cancelImport: () => void;
  retryFailed: () => Promise<void>;

  // Sorting
  setSortBy: (sortBy: ImportState['sortBy']) => void;
  setSortOrder: (order: ImportState['sortOrder']) => void;
}

// Default values
export const DEFAULT_IMPORT_SETTINGS: ImportSettings = {
  compressImages: true,
  compressionQuality: 85,
  generateThumbnails: true,
  thumbnailSize: 128,
  keepOriginalNames: true,
  autoDetectSpriteSheets: true,
  spriteSheetThreshold: { width: 256, height: 256 },
  createSubfolders: false,
  overwriteExisting: false,
  preserveMetadata: true,
};

// Supported file formats
export const SUPPORTED_FORMATS: Record<DetectedAssetType, string[]> = {
  image: ['.png', '.jpg', '.jpeg', '.gif', '.webp', '.bmp', '.svg'],
  audio: ['.mp3', '.ogg', '.wav', '.m4a', '.flac', '.aac'],
  script: ['.js', '.ts', '.jsx', '.tsx'],
  font: ['.ttf', '.otf', '.woff', '.woff2'],
  json: ['.json'],
  spine: ['.skel', '.atlas'],
  spritesheet: ['.json'], // Texture Packer, etc.
  unknown: [],
};

// File type icons
export const FILE_TYPE_ICONS: Record<DetectedAssetType, string> = {
  image: 'Image',
  audio: 'Music',
  script: 'FileCode',
  font: 'Type',
  json: 'FileJson',
  spine: 'Bone',
  spritesheet: 'Grid3X3',
  unknown: 'File',
};

// Utility functions
export function detectAssetType(file: File): DetectedAssetType {
  const ext = '.' + file.name.split('.').pop()?.toLowerCase();

  for (const [type, extensions] of Object.entries(SUPPORTED_FORMATS)) {
    if (extensions.includes(ext)) {
      return type as DetectedAssetType;
    }
  }

  return 'unknown';
}

export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
}

export function isImageFile(file: File): boolean {
  return SUPPORTED_FORMATS.image.some(ext =>
    file.name.toLowerCase().endsWith(ext)
  );
}

export function isAudioFile(file: File): boolean {
  return SUPPORTED_FORMATS.audio.some(ext =>
    file.name.toLowerCase().endsWith(ext)
  );
}
